import axios from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import * as qs from "qs";
import {
  Button as MuiButton,
  Checkbox as MuiCheckbox,
  CircularProgress as MuiCircularProgress,
  FormControl as MuiFormControl,
  FormControlLabel as MuiFormControlLabel,
  FormGroup as MuiFormGroup,
  Grid as MuiGrid,
  Paper as MuiPaper,
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableCell as MuiTableCell,
  TableContainer as MuiTableContainer,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
  TextField as MuiTextField,
  Typography as MuiTypography,
  Box as MuiBox,
} from "@mui/material";
import {
  AccessTime as MuiAccessTimeIcon,
  ErrorOutline as MuiErrorOutlineIcon,
  Star as MuiStarIcon,
  Warning as MuiWarningIcon,
} from "@mui/icons-material";
import { DataGridPro as MuiDataGrid } from "@mui/x-data-grid-pro";
import {
  DatePicker as MuiDatePicker,
  LocalizationProvider as MuiLocalizationProvider,
  DateTimePicker as MuiDateTimePicker,
  TimePicker as MuiKeyboardTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDateFns as MuiAdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { LoadingGridOverlay, NoRowsGridOverlay, useAlerts } from "common";
import { addMonths, addYears, format, parse, parseISO } from "date-fns";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Autocomplete } from "app/shared/UI/Autocomplete/Autocomplete";
import { conductOutreachPageContentStyles } from "./ConductOutreachPageStyles";
import { usePermissions } from "app/services/PermissionsService";
import { getOutreachErrorSummary } from "app/services/outreachServices";
import {
  DaysOfTheWeek,
  DefaultRecurringParam,
  DemographicOptimization,
  GET_STUDIES_URL_BASE,
  LOADER_TYPES,
  OutreachChannelModes,
  OutreachHistoryDefaults,
  OutreachScheduleModes,
  StudyChannelOptions,
  SummaryAccrual,
  URLs,
} from "app/shared/constants";
import {
  addIndexToElements,
  getDisplayPercentage,
  mapOutreachStatsToDisplayed,
  mapStudyToDisplayed,
} from "app/shared/utils";
import OutreachFailureSummary from "app/shared/UI/OutreachFailureSummary";
import ConfirmationDialog from "app/shared/UI/Dialog/ConfirmationDialog";
import { tableStyles, whiteTableHeaderLabel } from "app/shared/UI/sharedStyles";
import { defaultTableColumnHeaders } from "./ConductOutreachRecurringScheduleConfig";
import { defaultTableColumnHeadersHistory } from "./ConductOutreachHistoryConfig";

const errorSummaryContent = {
  primaryContent: "identifier",
  secondaryContent: "message",
};

const firstOutreachMinDate = new Date(Date.now() + 86400000);

const ConductOutreachPageContent = (props) => {
  const { studyId } = useParams();
  const [study, setStudy] = useState(null);
  const { t } = useTranslation();
  const { setAlert } = useAlerts();
  const [refresh, setRefresh] = useState(false);
  const [checkAvailabilityLoader, setCheckAvailabilityLoader] = useState(false);
  const [conductOutreachLoader, setConductOutreachLoader] = useState(false);
  const { permissions } = usePermissions();
  const navigate = useNavigate();

  const [cohortSources, setCohortSources] = useState([]);
  const [prospectCounts, setProspectCounts] = useState([]);
  const [recurringOutreachDate, setRecurringOutreachDate] = useState(
    () => new Date()
  );

  const [outreachHistory, setOutreachHistory] = useState([]);
  const [outreachStatistics, setOutreachStatistics] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedOutreach, setSelectedOutreach] = useState(-1);
  const [failureSummaryDialog, setFailureSummaryDialog] = useState(false);
  const [failedOutreach, setFailedOutreach] = useState(-1);
  const [openASConfirmation, setOpenASConfirmation] = useState(false);
  const [
    openASConfirmationForCancelRecurringWave,
    setOpenASConfirmationForCancelRecurringWave,
  ] = useState(false);
  const [
    openASConfirmationForDisabledChannels,
    setOpenASConfirmationForDisabledChannels,
  ] = useState(false);
  const [isSelectedChannelDisable, setIsSelectedChannelDisable] =
    useState(false);
  const [createOutreachChannelMode, setCreateOutreachChannelMode] = useState(
    OutreachChannelModes.DEFAULT
  );
  const [cancelOutreachRow, setCancelOutreachRow] = useState({});
  const [cancelRecurringOutreachRow, setCancelRecurringOutreachRow] = useState(
    {}
  );

  const [
    overrideExistingRecurringSchedule,
    setOverrideExistingRecurringSchedule,
  ] = useState(false);

  const [
    openASConfirmationForExistingRecurringSchedule,
    setOpenASConfirmationForExistingRecurringSchedule,
  ] = useState(false);

  const [recurringOutreach, setRecurringOutreach] = useState([]);

  const [outreachHistoryFilters, setOutreachHistoryFilters] = useState({
    sort: `${OutreachHistoryDefaults.OUTREACHHISTORYDEFAULTORDERBY},${OutreachHistoryDefaults.OUTREACHHISTORYDEFAULTORDER}`,
    page: OutreachHistoryDefaults.OUTREACHHISTORYDEFAULTPAGE,
    size: OutreachHistoryDefaults.OUTREACHHISTORYDEFAULTPAGESIZE,
  });
  const [sort, setSort] = useState({
    order: OutreachHistoryDefaults.OUTREACHHISTORYDEFAULTORDER,
    orderBy: OutreachHistoryDefaults.OUTREACHHISTORYDEFAULTORDERBY,
  });
  const [page, setPage] = useState(
    outreachHistoryFilters.page ||
    OutreachHistoryDefaults.OUTREACHHISTORYDEFAULTPAGE
  );
  const [rowsPerPage, setRowsPerPage] = useState(
    outreachHistoryFilters.size ||
    OutreachHistoryDefaults.OUTREACHHISTORYDEFAULTPAGESIZE
  );
  const [outreachHistoryTotalCount, setOutreachHistoryTotalCount] = useState(0);
  const [errorsList, setErrorList] = useState([]);
  const [
    openOutreachFailureSummaryDialog,
    setOpenOutreachFailureSummaryDialog,
  ] = useState(false);

  const initialLoading = {
    outreachStatisticsData: true,
    outreachRecurringData: true,
    outreachHistoryData: true,
    outreachFailureSummary: true,
  };

  const [isLoading, setIsLoading] = useState(initialLoading);
  // Pseudo-reducer to keep loading states updated
  const setLoader = (type, isSet) => {
    setIsLoading((prev) => ({
      ...prev,
      [type]: isSet,
    }));
  };

  const newErrorRecord = (type, identifier, message) => {
    return {
      type: type,
      identifier: identifier,
      message: message,
    };
  };

  const handleOutreachErrorContent = (outreach) => {
    getOutreachErrorSummary(
      outreach,
      setErrorList,
      setAlert,
      newErrorRecord,
      t,
      setLoader
    );
  };

  const getFirstOutreachDate = (channelName) => {
    if (channelName === StudyChannelOptions.MH) {
      return format(new Date(firstOutreachMinDate), "MM/dd/yyyy");
    }
    return format(new Date(), "MM/dd/yyyy");
  };

  const getOutreachDateAndTime = (channelName, channelEnable) => {
    if (channelName === StudyChannelOptions.MH && channelEnable) {
      return format(
        new Date(firstOutreachMinDate).setHours(0, 0, 0, 0),
        "MM/dd/yyyy hh:mm a"
      );
    }
    return format(new Date(), "MM/dd/yyyy hh:mm a");
  };

  const [formDataResponse] = useState({
    waveName: "",
    source: "",
    channel: "",
    outreachQty: "",
    outreachSchedule: "",
    outreachTime: format(new Date(), "MM/dd/yyyy hh:mm a"),
    firstOutreachDate: format(new Date(), "MM/dd/yyyy"),
    lastOutreachDate: format(addMonths(new Date(), 3), "MM/dd/yyyy"),
    outreachTimeOfDay: format(new Date(), "hh:mm a"),
    waveNote: "",
    scheduleRecurringCheckbox: [],
    templateName: "",
  });

  const formValidationScheme = Yup.object({
    waveName: Yup.string().trim().required(),
    templateName: Yup.lazy((values, parentOptions) => {
      return Yup.object().shape({
        templateId: Yup.number().when({
          is: () => {
            return parentOptions.context.channel
              ? parentOptions.context.channel?.type === "DE" &&
              permissions.MARKETO_ENABLED
              : false;
          },
          then: () => Yup.number().required().positive().integer(),
          otherwise: () => Yup.number().notRequired(),
        }),
      });
    }),
    source: Yup.object()
      .shape({
        id: Yup.number().required().positive().integer(),
      })
      .nullable(),
    channel: Yup.object().shape({
      type: Yup.string().trim().required(),
    }),
    outreachQty: Yup.number()
      .integer()
      .min(1, t("ConductOutreachPage.outreachQuantityErrorMessage"))
      .required(),
    outreachSchedule: Yup.string().required(),
    outreachTime: Yup.mixed().when("outreachSchedule", {
      is: "ONE_TIME_ONLY",
      then: () => Yup.mixed().required(),
      otherwise: undefined,
    }),
    firstOutreachDate: Yup.mixed().when("outreachSchedule", {
      is: "RECURRING",
      then: () => Yup.mixed().required(),
      otherwise: undefined,
    }),
    lastOutreachDate: Yup.mixed().when("outreachSchedule", {
      is: "RECURRING",
      then: () => Yup.mixed().required(),
      otherwise: undefined,
    }),
    outreachTimeOfDay: Yup.mixed().when("outreachSchedule", {
      is: "RECURRING",
      then: () =>
        Yup.mixed()
          .required()
          .when("channel", {
            is: (channel) => channel !== StudyChannelOptions.MH,
            then: () => Yup.mixed().required(),
            otherwise: undefined,
          }),
      otherwise: undefined,
    }),
    scheduleRecurringCheckbox: Yup.mixed().when("outreachSchedule", {
      is: "RECURRING",
      then: () => Yup.array().min(1),
      otherwise: undefined,
    }),
  });

  useEffect(() => {
    (async () => {
      if (studyId && permissions) {
        // fetch study by id
        try {
          const response = await axios.get(
            `${GET_STUDIES_URL_BASE}/${studyId}?vw=brief`
          );
          setStudy(mapStudyToDisplayed(response.data)); // save this for comparison at edit save
          let messages = [];
          if (permissions.MARKETO_ENABLED && !response.data.studyTemplates) {
            messages.push(t("ConductOutreachPage.warningMsg"));
          }
          if (response.data?.isStudySelfServiceActivated) {
            messages.push(t("messages.warning.studyTeamOutreachEnabled"));
          }

          if (messages.length > 0) {
            setAlert(
              "warning",
              <div>
                {messages.map((msg, index) => (
                  <div key={index}>{msg}</div>
                ))}
              </div>,
            );
          }
        } catch (err) {
          // set error.message in AlertContext
          setAlert("error", err.message);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setAlert, refresh, permissions]);

  useEffect(() => {
    const getCohortSources = async () => {
      if (!study) return;
      try {
        // fetch outreachable imports by protocolNumber and source
        const outreachRes = await axios.get(
          `${URLs.GET_OUTREACHABLE_URL_BASE}?vw=brief&protocolNumber=${study.protocolNumber}`
        );
        setCohortSources(outreachRes.data); // for cohort source dropdown
      } catch (err) {
        setAlert("error", err.message);
      }
    };

    getCohortSources();
  }, [study, setAlert]);

  useEffect(() => {
    const getOutreachHistory = async () => {
      if (!study) return;

      try {
        setLoader(LOADER_TYPES.OUTREACH_HISTORY_DATA, true);

        // creating new filters based on single sort and multi-sort
        let filters = {};
        if (Array.isArray(outreachHistoryFilters.sort)) {
          // used URLSearchParams to serialize params and send same param key twice with different values
          const params = new URLSearchParams();
          params.append("sort", outreachHistoryFilters.sort[0]);
          params.append("sort", outreachHistoryFilters.sort[1]);
          params.append("page", outreachHistoryFilters.page);
          params.append("size", outreachHistoryFilters.size);
          filters = params;
        } else {
          filters = outreachHistoryFilters;
        }

        const historyResponse = await axios.get(
          `${URLs.GET_OUTREACH_HISTORY_URL_BASE}/${study.id}`,
          { params: filters }
        );

        setOutreachHistory(historyResponse.data.values);
        setOutreachHistoryTotalCount(historyResponse.data.totalCount);
      } catch (err) {
        setAlert("error", err.message);
      } finally {
        setLoader(LOADER_TYPES.OUTREACH_HISTORY_DATA, false);
      }
    };

    getOutreachHistory();
  }, [study, outreachHistoryFilters, setAlert]);

  useEffect(() => {
    const fetchRecurringOutreach = async () => {
      if (!study) return;

      try {
        setLoader(LOADER_TYPES.OUTREACH_RECURRING_DATA, true);

        const recurringResponse = await axios.get(
          `${URLs.GET_OUTREACH_HISTORY_URL_BASE}/${study.id}${URLs.GET_OUTREACH_RECURRING_SCHEDULE_URL_BASE}`,
          {
            params: DefaultRecurringParam,
            paramsSerializer: {
              serialize: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
            },
          }
        );

        setRecurringOutreach(recurringResponse.data);
      } catch (err) {
        setAlert("error", err.message);
      } finally {
        setLoader(LOADER_TYPES.OUTREACH_RECURRING_DATA, false);
      }
    };

    fetchRecurringOutreach();
  }, [study, setAlert]);

  useEffect(() => {
    (async () => {
      // We want to refresh outreach statistics when outreach history changes
      // and we only care about refreshing the stats if there's some outreach
      // history already
      if (study && outreachHistory && outreachHistory.length > 0) {
        try {
          setLoader(LOADER_TYPES.OUTREACH_STATISTICS_DATA, true);

          const response = await axios.get(
            `${URLs.GET_OUTREACH_HISTORY_URL_BASE}/${study.id}${URLs.GET_OUTREACH_STATISTICS_URL_BASE}`
          );
          setOutreachStatistics(mapOutreachStatsToDisplayed(response.data)); // for Outreach Statistics
          // tally the total count instead of refreshing the study object from backend
          // this is to elimiate the infinite loop of rendering from study->outreachHistory->study

          setLoader(LOADER_TYPES.OUTREACH_STATISTICS_DATA, false);
        } catch (err) {
          // set error.message in AlertContext
          setAlert("error", err.message);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outreachHistory?.length, study]);

  const handleClickCheckAvailability = (values, setFieldValue) => {
    setCheckAvailabilityLoader(true);
    // check if a cohort source is selected
    if (!values.source.id) {
      // no cohort source selected
      setCheckAvailabilityLoader(false);
      setAlert("error", t(`ConductOutreachPage.checkAvailabilityAlert`));
    } else {
      // calling BE API /prospects/available-counts?importId=X
      // note: depends on the size of the cohort, this call may take longer
      // than the default 5 seconds so specifying an extended timeout here
      (async () => {
        try {
          const response = await axios.get(
            `${URLs.GET_OUTREACH_PROSPECT_COUNTS_URL_BASE}?importId=${values.source.id}`,
            {
              timeout: process.env.REACT_APP_OUTREACH_REQUEST_TIMEOUT,
            }
          );
          setProspectCounts(response.data); // prospect counts for all channels

          // This is to set outreach quantity when channel is already selected before checking availabity
          values.channel?.type &&
            setFieldValue(
              "outreachQty",
              getProspectCount(
                response.data,
                values.channel.type,
                "presetQuantity"
              )
            );
        } catch (err) {
          // set error.message in AlertContext
          setAlert("error", err.message);
        } finally {
          setCheckAvailabilityLoader(false);
        }
      })();
    }
  };

  const handleChangeChannel = async (setFieldValue, channelName) => {
    await setFieldValue("channel", channelName);
    setFieldValue(
      "outreachQty",
      getProspectCount(prospectCounts, channelName.type, "presetQuantity")
    );
    setCreateOutreachChannelMode(OutreachChannelModes.DEFAULT);
    setIsSelectedChannelDisable(false);
    setFieldValue("outreachSchedule", "");
  };

  const handleClickSubmit = (values, setSubmitting, resetForm) => {
    setConductOutreachLoader(true);
    // first check if outreach qty is <= available count
    if (
      values.outreachQty >
      getProspectCount(prospectCounts, values.channel.type, "available")
    ) {
      // alert message and return
      setAlert(
        "error",
        t(`ConductOutreachPage.validationErrorOutreachQtyGreaterThanAvailable`)
      );
      setSubmitting(false);
      setConductOutreachLoader(false);
      return;
    }
    handleCreateOutreach(
      values,
      overrideExistingRecurringSchedule,
      setSubmitting,
      resetForm
    );
  };

  const handleCreateOutreach = (
    values,
    existingRecurringSchedule,
    setSubmitting,
    resetForm
  ) => {
    let scheduleSummary = {};
    if (values.outreachSchedule === "RECURRING") {
      scheduleSummary = {
        endDate: format(new Date(values.lastOutreachDate), "yyyy-MM-dd"),
        overrideExistingSchedule: existingRecurringSchedule,
        recurringDays: values.scheduleRecurringCheckbox,
        recurringTime:
          values.channel.type !== StudyChannelOptions.MH
            ? format(new Date(recurringOutreachDate), "HH:mm:ss")
            : format(new Date().setHours(0, 0, 0, 0), "HH:mm:ss"),
        startDate: format(new Date(values.firstOutreachDate), "yyyy-MM-dd"),
        status: "ACTIVE",
      };
    }

    let newData = {
      waveName: values.waveName,
      importRequest: { id: values.source.id },
      channel: values.channel.type,
      emailTemplateId:
        values.channel.type === "DE" ? values.templateName.templateId : null,
      quantity: values.outreachQty,
      conductedDate:
        (values.outreachSchedule === "ONE_TIME_ONLY" &&
          new Date(values.outreachTime).toISOString()) ||
        null,
      note: values.waveNote,
      scheduleType: values.outreachSchedule,
      scheduleSummary: scheduleSummary,
    };

    Object.keys(newData).forEach((key) => {
      if (newData[key] === null) {
        delete newData[key];
      }
    });

    createOutreach(values, newData, setSubmitting, resetForm);
  };

  const createOutreach = async (values, newData, setSubmitting, resetForm) => {
    try {
      // note: depends on the size of the cohort, this call may take longer
      // than the default 5 seconds so specifying an extended timeout here
      await axios.post(
        `${URLs.GET_OUTREACH_URL_BASE}?mode=${createOutreachChannelMode}`,
        newData,
        {
          timeout: process.env.REACT_APP_OUTREACH_REQUEST_TIMEOUT,
        }
      );

      navigate(".", { state: null, replace: true });
      setRefresh((refresh) => !refresh);
      // reset the outreach fields
      handleClickReset(resetForm);
    } catch (err) {
      if (err.code === "ECONNABORTED") {
        setAlert("error", t("ConductOutreachPage.timeOutErrorMessage"));
      } else if (err.response && err.response.data) {
        if (
          err.response.data.status === 400 &&
          err.response.data.code === "INVALID_ACTION" &&
          err.response.data.detail.properties[0].name === "Conduct Outreach"
        ) {
          // set error.message in AlertContext
          setAlert(
            "error",
            t("ConductOutreachPage.errorMessageForDisabledChannels", {
              channelName: `"${t(
                "recruitmentPreference." + values.channel.type
              )}"`,
            })
          );
        } else if (
          err.response.data.status === 400 &&
          err.response.data.code === "INVALID_CHANNEL"
        ) {
          // set error.message in AlertContext
          setAlert(
            "error",
            t("ConductOutreachPage.errorMessageForInvalidChannels", {
              channelName: `"${t(
                "recruitmentPreference." + values.channel.type
              )}"`,
            })
          );
        } else if (
          err.response.data.status === 400 &&
          err.response.data.code === "INVALID_ACTION" &&
          err.response.data.detail.properties[0].name === "Outreach Schedule"
        ) {
          // set error.message in AlertContext
          setOpenASConfirmationForExistingRecurringSchedule(true);
        } else if (
          err.response.data.status === 400 &&
          err.response.data.code === "INVALID_VALUE" &&
          err.response.data.detail.properties[0].name === "waveName"
        ) {
          // set error.message in AlertContext
          setAlert(
            "error",
            t("ConductOutreachPage.errorMessageForUniqueWaveName")
          );
        } else if (
          err.response.data.status === 400 &&
          err.response.data.code === "INVALID_ACTION" &&
          err.response.data.detail.properties[0].name === "Select Prospects"
        ) {
          // set error.message in AlertContext
          setAlert(
            "error",
            t("ConductOutreachPage.errorMessageForNoProspectAvailable")
          );
        } else if (
          err.response.data.status === 400 &&
          err.response.data.code === "INVALID_VALUE" &&
          err.response.data.detail.properties[0].name === "quantity"
        ) {
          // set error.message in AlertContext
          setAlert(
            "error",
            `${err.response.data.detail.properties[0].name} ${err.response.data.detail.properties[0].message}`
          );
        } else {
          setAlert("error", err.message);
        }
      } else {
        // set error.message in AlertContext
        setAlert("error", err.message);
      }
      setSubmitting(false);
    } finally {
      setConductOutreachLoader(false);
    }
  };

  const handleClickReset = (resetForm) => {
    resetForm({ values: formDataResponse });
    setProspectCounts([]);
    setOverrideExistingRecurringSchedule(false);
    setIsSelectedChannelDisable(false);
  };

  const getRecruited = (data) => {
    if (data.protocolTargetAccrual > 0 && data.subjectSummary) {
      const cnt = getDisplayPercentage(
        (data.subjectSummary.enrolled * 100) / data.protocolTargetAccrual
      );
      return (
        cnt +
        "% (" +
        data.prospectOnStudy +
        "/" +
        data.protocolTargetAccrual +
        ")"
      );
    } else {
      return "";
    }
  };

  const getStatsDisplay = (data) => {
    if (data == null || data.value == null || data.percentage == null)
      return t(`NotApplicable`);

    return data.value + " (" + data.percentage + "%)";
  };

  const getCohortStatsDisplay = (data) => {
    if (data == null || data.value == null || data.percentage == null)
      return t(`NotApplicable`);

    return (
      getCohortName(parseInt(data.value)) +
      " (" +
      data.percentage +
      "%)"
    );
  };

  const getCohortName = (id) => {
    if (cohortSources.length === 0) return "";

    const found = cohortSources.find((item) => item.id === id);
    if (found) return found.name?.replaceAll("_", " ");

    return "";
  };

  const getProspectCount = (count, channelName, countType) => {
    if (count.length === 0) return "";

    const found = count.find((item) => item.type === channelName);
    if (found) return found[countType];

    return "NA";
  };

  const displayOutreachQuantityDialog = (index) => {
    setOpenDialog(true);
    setSelectedOutreach(index);
  };

  const closeOutreachQuantityDialog = () => {
    setOpenDialog(false);
    setSelectedOutreach(-1);
  };

  const displayFailureSummaryDialog = (index) => {
    setFailureSummaryDialog(true);
    setFailedOutreach(index);
  };

  const closeFailureSummaryDialog = () => {
    setFailureSummaryDialog(false);
    setFailedOutreach(-1);
  };

  const handleClickCancel = () => {
    setOpenASConfirmation(true);
  };
  const closeCancelASConfirmation = (cancelChoice) => {
    setOpenASConfirmation(false);
    // API call to cancel scheduled outreach will go here.
    if (cancelChoice) {
      (async () => {
        try {
          await axios({
            url: `${URLs.GET_OUTREACH_URL_BASE}/${cancelOutreachRow.id}${URLs.GET_OUTREACH_CANCEL_URL_BASE}`,
            method: "PUT",
          });

          setCancelOutreachRow({});
          navigate(".", { state: null, replace: true });
          setRefresh((refresh) => !refresh);
        } catch (err) {
          // set error.message in AlertContext
          setAlert("error", err.message);
        }
      })();
    }
  };

  const handleClickCancelRecurring = () => {
    setOpenASConfirmationForCancelRecurringWave(true);
  };
  const closeCancelASConfirmationForCancelRecurringWave = (cancelChoice) => {
    setOpenASConfirmationForCancelRecurringWave(false);
    // API call to cancel scheduled outreach will go here.
    if (cancelChoice) {
      (async () => {
        try {
          await axios({
            url: `${URLs.GET_OUTREACH_URL_BASE}${URLs.GET_OUTREACH_RECURRING_SCHEDULE_URL_BASE}/${cancelRecurringOutreachRow.id}${URLs.GET_OUTREACH_CANCEL_URL_BASE}`,
            method: "PUT",
          });

          setCancelRecurringOutreachRow({});
          navigate(".", { state: null, replace: true });
          setRefresh((refresh) => !refresh);
        } catch (err) {
          // set error.message in AlertContext
          setAlert("error", err.message);
        }
      })();
    }
  };

  const closeCancelASConfirmationForDisabledChannels = (cancelChoice) => {
    setOpenASConfirmationForDisabledChannels(false);
    if (cancelChoice) {
      setCreateOutreachChannelMode(OutreachChannelModes.STANDARD);
    } else {
      setCreateOutreachChannelMode(OutreachChannelModes.AUTOMATED);
    }
  };

  const closeCancelASConfirmationForExistingRecurring = (
    proceedChoice,
    resetForm,
    values,
    setSubmitting
  ) => {
    setOpenASConfirmationForExistingRecurringSchedule(false);
    if (proceedChoice) {
      setConductOutreachLoader(true);
      setOverrideExistingRecurringSchedule(true);
      handleCreateOutreach(values, true, setSubmitting, resetForm);
    } else {
      setOverrideExistingRecurringSchedule(false);
      handleClickReset(resetForm);
    }
  };

  const handleSortChange = (sortModel) => {
    setSort({
      orderBy: sortModel[0].field,
      order: sortModel[0].sort,
    });

    setOutreachHistoryFilters({
      ...outreachHistoryFilters,
      sort: `${sortModel[0].field},${sortModel[0].sort}`,
    });
  };

  const handlePaginationModelChange = (model) => {
    setPage(model.page);
    setRowsPerPage(model.pageSize);
    setOutreachHistoryFilters({
      ...outreachHistoryFilters,
      page: model.page,
      size: model.pageSize,
    });
  };

  const closeOutreachFailureSummary = () => {
    setOpenOutreachFailureSummaryDialog(false);
  };

  const setTemplateNameValue = (setFieldTouched, setFieldValue) => {
    if (study.studyTemplates?.length === 0) {
      setFieldTouched("templateName", true);
    } else if (study.studyTemplates?.length === 1) {
      setFieldValue("templateName", study.studyTemplates[0]);
    } else {
      setFieldValue(
        "templateName",
        study.studyTemplates?.find((template) => template.isDefault === true)
      );
    }
  };

  const NoStudyOverlay = () => (
    <NoRowsGridOverlay
      message={!recurringOutreach.length && t(`common.emptyTableRow`)}
    />
  );

  const addTotalRowToElement = (outreachHistory = [], study = {}) => {
    const {
      outreachSummary = {},
      responseSummary = {},
      subjectSummary = {},
    } = study;

    const totalRow = {
      id: "total-row",
      isTotal: true,
      outreachSummary,
      responseSummary,
      subjectSummary,
    };

    return [totalRow, ...outreachHistory];
  };

  return study ? (
    <div style={conductOutreachPageContentStyles.root}>
      {openOutreachFailureSummaryDialog && (
        <OutreachFailureSummary
          onClose={closeOutreachFailureSummary}
          open={openOutreachFailureSummaryDialog}
          isDisplayConductOutreachButton={false}
          studyId={study.id}
          outreachRefresh={refresh}
          setOutreachRefresh={setRefresh}
          navigate={navigate}
        />
      )}
      <ConfirmationDialog
        open={openASConfirmation}
        onClose={closeCancelASConfirmation}
        message={
          cancelOutreachRow.initiationMode === "USER_INITIATED"
            ? t("ConductOutreachPage.confirmationMessageCancel", {
              waveName: cancelOutreachRow && cancelOutreachRow.waveName,
            })
            : t("ConductOutreachPage.confirmationMessageCancelRecurring", {
              waveName: cancelOutreachRow && cancelOutreachRow.waveName,
            })
        }
        okLabel={t("common.yesLabel")}
        cancelLabel={t("common.noLabel")}
      />
      <Formik
        enableReinitialize
        initialValues={formDataResponse}
        validationSchema={formValidationScheme}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          handleClickSubmit(values, setSubmitting, resetForm);
          setSubmitting(false);
        }}
      >
        {({
          values,
          errors,
          isValid,
          dirty,
          isSubmitting,
          handleChange,
          setFieldValue,
          touched,
          handleBlur,
          resetForm,
          validateField,
          setFieldTouched,
          setSubmitting,
        }) => {
          const valueForOutReach = (outreachValue) => {
            if (outreachValue === "ONE_TIME_ONLY") {
              return "Schedule One Time Only";
            } else if (outreachValue === "RECURRING") {
              return "Schedule Recurring";
            }
          };
          return (
            <Form>
              <ConfirmationDialog
                open={openASConfirmationForDisabledChannels}
                onClose={closeCancelASConfirmationForDisabledChannels}
                message={t(
                  "ConductOutreachPage.confirmationMessageForDisabledChannel",
                  {
                    channelName: `"${t(
                      "recruitmentPreference." + values.channel.type
                    )}"`,
                  }
                )}
                okLabel={t("common.yesLabel")}
                cancelLabel={t("common.noLabel")}
              />
              <ConfirmationDialog
                open={openASConfirmationForExistingRecurringSchedule}
                onClose={(proceedChoice) =>
                  closeCancelASConfirmationForExistingRecurring(
                    proceedChoice,
                    resetForm,
                    values,
                    setSubmitting
                  )
                }
                message={t(
                  "ConductOutreachPage.errorMessageForExistingRecurring"
                )}
                okLabel={t("ConductOutreachPage.proceedLabel")}
                cancelLabel={t("common.noLabel")}
              />
              <ConfirmationDialog
                open={openASConfirmationForCancelRecurringWave}
                onClose={closeCancelASConfirmationForCancelRecurringWave}
                message={t(
                  "ConductOutreachPage.confirmationMessageCancelRecurringSchedule",
                  {
                    waveName:
                      cancelRecurringOutreachRow &&
                      cancelRecurringOutreachRow.waveName,
                  }
                )}
                okLabel={t("common.yesLabel")}
                cancelLabel={t("common.noLabel")}
              />
              <MuiGrid container size={6} mb="15px">
                <MuiGrid sx={{ width: "240px" }}>
                  <MuiTypography>
                    {t(`formLabel.irb`)}{" "}
                    <MuiTypography variant="subtitle1" display="inline">
                      {study.irbNumber}
                    </MuiTypography>
                  </MuiTypography>
                  <MuiTypography>
                    {t(`formLabel.protocolNumber`)}: &nbsp;
                    <MuiTypography variant="subtitle1" display="inline">
                      {study.protocolNumber}
                    </MuiTypography>
                  </MuiTypography>
                </MuiGrid>
                <MuiGrid size={2}>
                  <MuiTypography>
                    {`${t("formLabel.summaryAccrual")}:`}{" "}
                    <MuiTypography variant="subtitle1" display="inline">
                      {study && study.protocolSummaryAccrual
                        ? SummaryAccrual.SummaryAccrualTrue
                        : SummaryAccrual.SummaryAccrualFalse}
                    </MuiTypography>
                  </MuiTypography>
                  <MuiTypography>
                    {`${t("formLabel.studyNickname")}: `}{" "}
                    <MuiTypography variant="subtitle1" display="inline">
                      {study.nickname}
                    </MuiTypography>
                  </MuiTypography>
                </MuiGrid>
                <MuiGrid size={2}>
                  <MuiTypography>
                    {`${t("formLabel.demographicOptimization")}:`}{" "}
                    <MuiTypography variant="subtitle1" display="block">
                      {
                        DemographicOptimization[
                        study.demographicOptimizationStatus
                        ]
                      }
                    </MuiTypography>
                  </MuiTypography>
                </MuiGrid>
              </MuiGrid>
              <MuiGrid
                container
                sx={conductOutreachPageContentStyles.gridContainer}
              >
                <MuiGrid
                  size={5}
                  sx={conductOutreachPageContentStyles.gridSection}
                >
                  <MuiTypography
                    sx={conductOutreachPageContentStyles.customMarginBottom}
                  >
                    {t(`ConductOutreachPage.recommendationLabel`)}
                  </MuiTypography>
                  <MuiGrid
                    container
                    display="flex"
                    justifyContent="space-between"
                  >
                    <MuiGrid size={6}>
                      <MuiTypography>
                        {`${t("studyTable.head_label_recruited")}: `}{" "}
                        <MuiTypography variant="subtitle1" display="inline">
                          {getRecruited(study)}
                        </MuiTypography>
                      </MuiTypography>
                    </MuiGrid>
                    <MuiGrid size={6}>
                      <MuiTypography>
                        {`${t("studyTable.head_label_time_elapsed")}: `}{" "}
                        <MuiTypography variant="subtitle1" display="inline">
                          {study.timeElapsed}
                        </MuiTypography>
                      </MuiTypography>
                    </MuiGrid>
                  </MuiGrid>
                  <MuiGrid
                    container
                    display="flex"
                    justifyContent="space-between"
                  >
                    <MuiGrid size={6}>
                      <MuiTypography>
                        {`${t("formLabel.riskCount")}: `}{" "}
                        <MuiTypography variant="subtitle1" display="inline">
                          {study.riskCount}
                        </MuiTypography>
                      </MuiTypography>
                    </MuiGrid>
                    <MuiGrid size={6}>
                      <MuiTypography>
                        {`${t("studyTable.head_label_study_end_date")}: `}{" "}
                        <MuiTypography variant="subtitle1" display="inline">
                          {study.recruitmentEndDate &&
                            format(
                              parseISO(study.recruitmentEndDate),
                              "MM/dd/yyyy"
                            )}
                        </MuiTypography>
                      </MuiTypography>
                    </MuiGrid>
                  </MuiGrid>
                  <MuiTable
                    sx={conductOutreachPageContentStyles.outreachTable}
                    size="small"
                  >
                    <MuiTableHead sx={whiteTableHeaderLabel}>
                      <MuiTableRow>
                        <MuiTableCell />
                        <MuiTableCell
                          sx={conductOutreachPageContentStyles.bestHeaderCell}
                        >
                          <div>
                            <MuiStarIcon fontSize="small" />
                            <span>
                              {t(`ConductOutreachPage.recommendationBestLabel`)}
                            </span>
                          </div>
                        </MuiTableCell>
                        <MuiTableCell
                          sx={conductOutreachPageContentStyles.worstHeaderCell}
                        >
                          <div>
                            <MuiWarningIcon fontSize="small" />
                            <span>
                              {t(
                                `ConductOutreachPage.recommendationWorstLabel`
                              )}
                            </span>
                          </div>
                        </MuiTableCell>
                      </MuiTableRow>
                    </MuiTableHead>
                    <MuiTableBody>
                      <MuiTableRow>
                        <MuiTableCell>
                          {t(`ConductOutreachPage.recommendationPerformingDay`)}
                        </MuiTableCell>
                        <MuiTableCell>
                          {getStatsDisplay(outreachStatistics.dayOfWeekBest)}
                        </MuiTableCell>
                        <MuiTableCell>
                          {getStatsDisplay(outreachStatistics.dayOfWeekWorst)}
                        </MuiTableCell>
                      </MuiTableRow>
                      <MuiTableRow>
                        <MuiTableCell>
                          {t(
                            `ConductOutreachPage.recommendationPerformingHour`
                          )}
                        </MuiTableCell>
                        <MuiTableCell>
                          {getStatsDisplay(outreachStatistics.hourBest)}
                        </MuiTableCell>
                        <MuiTableCell>
                          {getStatsDisplay(outreachStatistics.hourWorst)}
                        </MuiTableCell>
                      </MuiTableRow>
                      <MuiTableRow>
                        <MuiTableCell>
                          {t(
                            `ConductOutreachPage.recommendationPerformingChannel`
                          )}
                        </MuiTableCell>
                        <MuiTableCell>
                          {getStatsDisplay(outreachStatistics.channelBest)}
                        </MuiTableCell>
                        <MuiTableCell>
                          {getStatsDisplay(outreachStatistics.channelWorst)}
                        </MuiTableCell>
                      </MuiTableRow>
                      <MuiTableRow>
                        <MuiTableCell>
                          {t(
                            `ConductOutreachPage.recommendationPerformingLotSize`
                          )}
                        </MuiTableCell>
                        <MuiTableCell>
                          {getStatsDisplay(outreachStatistics.quantityBest)}
                        </MuiTableCell>
                        <MuiTableCell>
                          {getStatsDisplay(outreachStatistics.quantityWorst)}
                        </MuiTableCell>
                      </MuiTableRow>
                      <MuiTableRow>
                        <MuiTableCell>
                          {t(
                            `ConductOutreachPage.recommendationPerformingCohort`
                          )}
                        </MuiTableCell>
                        <MuiTableCell
                          sx={
                            conductOutreachPageContentStyles.performingCohortTableCell
                          }
                        >
                          {getCohortStatsDisplay(
                            outreachStatistics.importRequestBest
                          )}
                        </MuiTableCell>
                        <MuiTableCell
                          sx={
                            conductOutreachPageContentStyles.performingCohortTableCell
                          }
                        >
                          {getCohortStatsDisplay(
                            outreachStatistics.importRequestWorst
                          )}
                        </MuiTableCell>
                      </MuiTableRow>
                    </MuiTableBody>
                  </MuiTable>
                </MuiGrid>
                <MuiGrid
                  size={6}
                  sx={conductOutreachPageContentStyles.gridSection}
                >
                  <MuiTypography
                    sx={conductOutreachPageContentStyles.customMarginBottom}
                  >
                    {t(`ConductOutreachPage.outreachSettingsLabel`)}
                  </MuiTypography>
                  <MuiGrid size={12}>
                    <MuiTextField
                      name="waveName"
                      sx={conductOutreachPageContentStyles.formTextField}
                      label={t(`ConductOutreachPage.waveNameLabel`)}
                      variant="outlined"
                      value={values.waveName}
                      size="small"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={!!errors.waveName && !!touched.waveName}
                      helperText={
                        errors.waveName && !!touched.waveName
                          ? t("formValidation.requiredField")
                          : ""
                      }
                    />
                  </MuiGrid>
                  <MuiGrid
                    container
                    sx={conductOutreachPageContentStyles.gridContainer}
                  >
                    <MuiGrid size={8}>
                      <Autocomplete
                        id="source"
                        key="source"
                        size="small"
                        label={t(`ConductOutreachPage.cohortSourceLabel`)}
                        options={cohortSources || []}
                        getOptionLabel={(option) =>
                          option ? option.name?.replaceAll("_", " ") : ""
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.id === value?.id
                        }
                        variant="outlined"
                        margin="dense"
                        value={values.source}
                        onBlur={handleBlur}
                        onChange={async (evt, newValue) => {
                          await setFieldValue("source", newValue || null);
                          setProspectCounts([]);

                          validateField("source");
                        }}
                        clientSide={true}
                        error={errors.source && touched.source}
                        helperText={
                          errors.source && touched.source
                            ? t("formValidation.requiredField")
                            : ""
                        }
                      />
                    </MuiGrid>
                    <MuiGrid container justifyContent="flex-end" size={4}>
                      <div style={conductOutreachPageContentStyles.wrapper}>
                        <MuiButton
                          variant="contained"
                          color="primary"
                          disabled={checkAvailabilityLoader}
                          onClick={() =>
                            handleClickCheckAvailability(values, setFieldValue)
                          }
                        >
                          {t(`ConductOutreachPage.availabilityButtonLabel`)}
                        </MuiButton>
                        {checkAvailabilityLoader && (
                          <MuiCircularProgress
                            size={24}
                            sx={conductOutreachPageContentStyles.buttonProgress}
                          />
                        )}
                      </div>
                    </MuiGrid>
                  </MuiGrid>
                  <MuiTableContainer
                    sx={conductOutreachPageContentStyles.tableContainerRoot}
                  >
                    <MuiTable size="small">
                      <MuiTableHead
                        sx={{
                          ...conductOutreachPageContentStyles.tableHead,
                          ...whiteTableHeaderLabel,
                        }}
                      >
                        <MuiTableRow>
                          <MuiTableCell />
                          <MuiTableCell align="right">
                            {t(`ConductOutreachPage.emailLabel`)}
                          </MuiTableCell>
                          <MuiTableCell align="right">
                            {t(`ConductOutreachPage.mychartLabel`)}
                          </MuiTableCell>
                          <MuiTableCell align="right">
                            {t(`ConductOutreachPage.myhealthLabel`)}
                          </MuiTableCell>
                          <MuiTableCell align="right">
                            {t(`ConductOutreachPage.manualLabel`)}
                          </MuiTableCell>
                        </MuiTableRow>
                      </MuiTableHead>
                      <MuiTableBody>
                        <MuiTableRow>
                          <MuiTableCell
                            sx={conductOutreachPageContentStyles.tableHead}
                          >
                            {t(`ConductOutreachPage.availableLabel`)}
                          </MuiTableCell>
                          {StudyChannelOptions &&
                            Object.keys(StudyChannelOptions).map(
                              (channelValue, idx) => (
                                <MuiTableCell key={idx} align="right">
                                  <MuiTypography variant="subtitle1">
                                    {getProspectCount(
                                      prospectCounts,
                                      channelValue,
                                      "available"
                                    )}
                                  </MuiTypography>
                                </MuiTableCell>
                              )
                            )}
                        </MuiTableRow>
                        <MuiTableRow>
                          <MuiTableCell
                            sx={conductOutreachPageContentStyles.tableHead}
                          >
                            {t(`ConductOutreachPage.recommendedLabel`)}
                          </MuiTableCell>
                          {StudyChannelOptions &&
                            Object.keys(StudyChannelOptions).map(
                              (channelValue, idx) => (
                                <MuiTableCell key={idx} align="right">
                                  <MuiTypography variant="subtitle1">
                                    {getProspectCount(
                                      prospectCounts,
                                      channelValue,
                                      "recommended"
                                    )}
                                  </MuiTypography>
                                </MuiTableCell>
                              )
                            )}
                        </MuiTableRow>
                      </MuiTableBody>
                    </MuiTable>
                  </MuiTableContainer>
                  <MuiGrid
                    container
                    sx={conductOutreachPageContentStyles.customMarginBottom}
                    justifyContent="space-between"
                    size={12}
                  >
                    <MuiGrid size={3}>
                      <Autocomplete
                        id="channel"
                        key="channel"
                        size="small"
                        label={t(`ConductOutreachPage.channelLabel`)}
                        options={study.channelStatus || []}
                        getOptionDisabled={(option) =>
                          option.status ===
                          t(`ConductOutreachPage.channelStatus`)
                        }
                        getOptionLabel={(option) => {
                          return option.type
                            ? t("recruitmentPreference." + option.type)
                            : "";
                        }}
                        isOptionEqualToValue={(option, value) =>
                          option.type === value?.type
                        }
                        variant="outlined"
                        margin="dense"
                        value={values.channel}
                        onBlur={handleBlur}
                        onChange={(evt, newValue) => {
                          const channelName = newValue || "";

                          // set templateName based on available study templates and Marketo permissions
                          channelName.type === "DE" &&
                            permissions.MARKETO_ENABLED &&
                            setTemplateNameValue(
                              setFieldTouched,
                              setFieldValue
                            );

                          handleChangeChannel(setFieldValue, channelName);
                          setFieldValue(
                            "firstOutreachDate",
                            getFirstOutreachDate(channelName.type ?? "")
                          );
                          setFieldValue(
                            "outreachTime",
                            getOutreachDateAndTime(
                              channelName.type ?? "",
                              !isSelectedChannelDisable
                            )
                          );
                        }}
                        clientSide={true}
                        error={errors.channel && touched.channel ? true : false}
                        helperText={
                          errors.channel && touched.channel
                            ? t("formValidation.requiredField")
                            : ""
                        }
                      />
                    </MuiGrid>
                    <MuiGrid
                      size={2}
                      sx={conductOutreachPageContentStyles.textFieldWidth}
                    >
                      <MuiTextField
                        id="outreachQty"
                        key="outreachQty"
                        type="number"
                        label={t(`ConductOutreachPage.outreachQtyLabel`)}
                        variant="outlined"
                        value={values.outreachQty}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        margin="dense"
                        sx={conductOutreachPageContentStyles.selectMarginDense}
                        size="small"
                        error={touched.outreachQty && errors.outreachQty}
                        helperText={
                          touched.outreachQty &&
                          errors.outreachQty &&
                          t("ConductOutreachPage.outreachQuantityErrorMessage")
                        }
                      />
                    </MuiGrid>
                    <MuiGrid size={4}>
                      <Autocomplete
                        id="outreachSchedule"
                        key="outreachSchedule"
                        name="outreachSchedule"
                        size="small"
                        label={t(`ConductOutreachPage.outreachScheduleLabel`)}
                        options={Object.values(OutreachScheduleModes) || []}
                        getOptionLabel={(option) => t(option)}
                        getOptionDisabled={(option) =>
                          (isSelectedChannelDisable ||
                            (values.channel.type !== StudyChannelOptions.MC &&
                              values.channel.type !==
                              StudyChannelOptions.MH)) &&
                          option === "ConductOutreachPage.scheduleRecurring"
                        }
                        variant="outlined"
                        margin="dense"
                        value={
                          values.outreachSchedule
                            ? valueForOutReach(values.outreachSchedule)
                            : ""
                        }
                        onChange={(e, newValue) => {
                          setFieldTouched("outreachSchedule", true);

                          const selectedValue = Object.keys(
                            OutreachScheduleModes
                          ).find(
                            (key) => OutreachScheduleModes[key] === newValue
                          );
                          selectedValue &&
                            setFieldValue("outreachSchedule", selectedValue);
                        }}
                        clientSide={true}
                        error={
                          Boolean(errors.outreachSchedule) &&
                          touched.outreachSchedule
                        }
                        helperText={
                          errors.outreachSchedule && touched.outreachSchedule
                            ? t("formValidation.requiredField")
                            : ""
                        }
                      />
                    </MuiGrid>
                  </MuiGrid>
                  <MuiTextField
                    sx={conductOutreachPageContentStyles.formTextField}
                    name="waveNote"
                    label={t(`formLabel.note`)}
                    variant="outlined"
                    multiline={true}
                    size="small"
                    value={values.waveNote}
                    onChange={handleChange}
                  />
                  {values.channel.type === "DE" &&
                    permissions.MARKETO_ENABLED && (
                      <MuiGrid
                        size={4}
                        sx={conductOutreachPageContentStyles.customMarginBottom}
                      >
                        <Autocomplete
                          id="templateName"
                          name="templateName"
                          key="templateName"
                          size="small"
                          label={t(`ConductOutreachPage.emailTemplateLabel`)}
                          options={study.studyTemplates || []}
                          getOptionLabel={(option) => option.templateName}
                          isOptionEqualToValue={(option, value) =>
                            option.templateName === value?.templateName
                          }
                          variant="outlined"
                          margin="dense"
                          value={values.templateName}
                          onBlur={handleBlur}
                          required
                          onChange={async (e, newValue) => {
                            await setFieldTouched("templateName", true);
                            setFieldValue("templateName", newValue || "");
                          }}
                          clientSide={true}
                          error={Boolean(
                            errors.templateName && touched.templateName
                          )}
                          helperText={
                            errors.templateName && touched.templateName
                              ? t("formValidation.requiredField")
                              : ""
                          }
                        />
                      </MuiGrid>
                    )}
                  {values.outreachSchedule === "ONE_TIME_ONLY" && (
                    <MuiGrid
                      container
                      sx={conductOutreachPageContentStyles.customMarginBottom}
                      justifyContent="space-between"
                      size={12}
                    >
                      <MuiLocalizationProvider dateAdapter={MuiAdapterDateFns}>
                        {values.channel.type === StudyChannelOptions.MH &&
                          !isSelectedChannelDisable ? (
                          <MuiDatePicker
                            disableToolbar
                            variant="inline"
                            inputVariant="outlined"
                            label={t(
                              "ConductOutreachPage.outreachDateLabelForMH"
                            )}
                            format="MM/dd/yyyy"
                            margin="dense"
                            disablePast
                            value={new Date(values.outreachTime)}
                            onBlur={handleBlur}
                            onChange={(date) => {
                              setFieldValue("outreachTime", date);
                            }}
                            minDate={firstOutreachMinDate}
                            id="outreachTime"
                            name="outreachTime"
                            slotProps={{
                              textField: {
                                variant: "outlined",
                                size: "small",
                                InputLabelProps: { shrink: true },
                              },
                            }}
                            inputProps={{ autoComplete: "off" }}
                            minDateMessage={t(
                              "ConductOutreachPage.firstOutreachDateMinDateMessage"
                            )}
                            error={
                              !!errors.outreachTime && !!touched.outreachTime
                            }
                            helperText={
                              !!errors.outreachTime && !!touched.outreachTime
                                ? t("formValidation.requiredField")
                                : ""
                            }
                          />
                        ) : (
                          <MuiDateTimePicker
                            sx={conductOutreachPageContentStyles.timePicker}
                            hideTabs
                            autoOk
                            variant="inline"
                            inputVariant="outlined"
                            label={t("ConductOutreachPage.outreachDateLabel")}
                            format="MM/dd/yyyy hh:mm a"
                            margin="dense"
                            value={new Date(values.outreachTime)}
                            minDate={new Date(values.outreachTime)}
                            onBlur={handleBlur}
                            onChange={(date) => {
                              setFieldValue(
                                "outreachTime",
                                format(date, "MM/dd/yyyy hh:mm a")
                              );
                            }}
                            id="outreachTime"
                            name="outreachTime"
                            required={true}
                            slotProps={{
                              textField: {
                                variant: "outlined",
                                size: "small",
                                InputLabelProps: { shrink: true },
                              },
                            }}
                            inputProps={{ autoComplete: "off" }}
                            error={
                              !!errors.outreachTime && !!touched.outreachTime
                            }
                            helperText={
                              !!errors.outreachTime && !!touched.outreachTime
                                ? t("formValidation.requiredField")
                                : ""
                            }
                          />
                        )}
                      </MuiLocalizationProvider>
                    </MuiGrid>
                  )}
                  {values.outreachSchedule === "RECURRING" && (
                    <>
                      <MuiLocalizationProvider dateAdapter={MuiAdapterDateFns}>
                        <MuiGrid
                          sx={
                            conductOutreachPageContentStyles.customMarginBottom
                          }
                          container
                          justifyContent="space-between"
                        >
                          <MuiGrid size={4}>
                            <MuiDatePicker
                              disableToolbar
                              variant="inline"
                              inputVariant="outlined"
                              required={true}
                              label={t(
                                "ConductOutreachPage.firstOutreachDateLabel"
                              )}
                              format="MM/dd/yyyy"
                              margin="dense"
                              disablePast
                              value={new Date(values.firstOutreachDate)}
                              minDate={
                                values.channel.type === StudyChannelOptions.MH
                                  ? firstOutreachMinDate
                                  : new Date()
                              }
                              onBlur={handleBlur}
                              onChange={(date) => {
                                setFieldValue("firstOutreachDate", date);
                              }}
                              id="firstOutreachDate"
                              name="firstOutreachDate"
                              minDateMessage={t(
                                "ConductOutreachPage.firstOutreachDateMinDateMessage"
                              )}
                              fullWidth
                              slotProps={{
                                textField: {
                                  variant: "outlined",
                                  size: "small",
                                  InputLabelProps: { shrink: true },
                                },
                              }}
                              inputProps={{ autoComplete: "off" }}
                              error={
                                !!errors.firstOutreachDate &&
                                !!touched.firstOutreachDate
                              }
                              helperText={
                                !!errors.firstOutreachDate &&
                                  !!touched.firstOutreachDate
                                  ? t("formValidation.requiredField")
                                  : ""
                              }
                            />
                          </MuiGrid>
                          {values.channel.type !== StudyChannelOptions.MH && (
                            <MuiGrid size={3}>
                              <MuiKeyboardTimePicker
                                margin="dense"
                                variant="inline"
                                inputVariant="outlined"
                                required={true}
                                mask="__:__ _M"
                                label={t(
                                  "ConductOutreachPage.outreachTimeOfDay"
                                )}
                                inputValue={values.outreachTimeOfDay}
                                value={parse(
                                  values.outreachTimeOfDay,
                                  "hh:mm a",
                                  new Date()
                                )}
                                onBlur={handleBlur}
                                onChange={(date) => {
                                  setRecurringOutreachDate(date);
                                  setFieldValue(
                                    "outreachTimeOfDay",
                                    format(date, "hh:mm a")
                                  );
                                }}
                                id="outreachTimeOfDay"
                                name="outreachTimeOfDay"
                                fullWidth
                                keyboardIcon={
                                  <MuiAccessTimeIcon fontSize="small" />
                                }
                                slotProps={{
                                  textField: {
                                    variant: "outlined",
                                    size: "small",
                                    InputLabelProps: { shrink: true },
                                  },
                                }}
                                inputProps={{ autoComplete: "off" }}
                                error={
                                  !!errors.outreachTimeOfDay &&
                                  !!touched.outreachTimeOfDay
                                }
                                helperText={
                                  !!errors.outreachTimeOfDay &&
                                    !!touched.outreachTimeOfDay
                                    ? t("formValidation.requiredField")
                                    : ""
                                }
                              />
                            </MuiGrid>
                          )}
                          <MuiGrid size={4}>
                            <MuiDatePicker
                              disableToolbar
                              variant="inline"
                              inputVariant="outlined"
                              required={true}
                              label={t(
                                "ConductOutreachPage.lastOutreachDateLabel"
                              )}
                              format="MM/dd/yyyy"
                              margin="dense"
                              disablePast
                              value={new Date(values.lastOutreachDate)}
                              onBlur={handleBlur}
                              onChange={(date) => {
                                setFieldValue("lastOutreachDate", date);
                              }}
                              id="lastOutreachDate"
                              name="lastOutreachDate"
                              maxDate={addYears(
                                new Date(values.firstOutreachDate),
                                1
                              )}
                              minDate={new Date(values.firstOutreachDate)}
                              minDateMessage={t(
                                "ConductOutreachPage.lastOutreachDateMinDateMessage"
                              )}
                              maxDateMessage={t(
                                "ConductOutreachPage.lastOutreachDateMaxDateMessage"
                              )}
                              fullWidth
                              slotProps={{
                                textField: {
                                  variant: "outlined",
                                  size: "small",
                                  InputLabelProps: { shrink: true },
                                },
                              }}
                              inputProps={{ autoComplete: "off" }}
                              error={
                                !!errors.firstOutreachDate &&
                                !!touched.firstOutreachDate
                              }
                              helperText={
                                !!errors.firstOutreachDate &&
                                  !!touched.firstOutreachDate
                                  ? t("formValidation.requiredField")
                                  : ""
                              }
                            />
                          </MuiGrid>
                        </MuiGrid>
                      </MuiLocalizationProvider>
                      <MuiFormControl
                        sx={conductOutreachPageContentStyles.customMarginBottom}
                        component="fieldset"
                      >
                        <MuiTypography color="textSecondary">
                          {t("ConductOutreachPage.scheduleRecurringLabel")}
                        </MuiTypography>
                        <MuiFormGroup row={true}>
                          <MuiFormControlLabel
                            control={
                              <MuiCheckbox
                                name={"scheduleRecurringCheckbox"}
                                color="pepGray"
                                value={DaysOfTheWeek[0]}
                                onChange={handleChange}
                                checked={values.scheduleRecurringCheckbox?.includes(
                                  DaysOfTheWeek[0]
                                )}
                                size="small"
                              />
                            }
                            label={t("ConductOutreachPage.sundayLabel")}
                          />
                          <MuiFormControlLabel
                            control={
                              <MuiCheckbox
                                name={"scheduleRecurringCheckbox"}
                                color="pepGray"
                                value={DaysOfTheWeek[1]}
                                onChange={handleChange}
                                checked={values.scheduleRecurringCheckbox?.includes(
                                  DaysOfTheWeek[1]
                                )}
                                size="small"
                              />
                            }
                            label={t("ConductOutreachPage.mondayLabel")}
                          />
                          <MuiFormControlLabel
                            control={
                              <MuiCheckbox
                                name={"scheduleRecurringCheckbox"}
                                color="pepGray"
                                value={DaysOfTheWeek[2]}
                                onChange={handleChange}
                                checked={values.scheduleRecurringCheckbox?.includes(
                                  DaysOfTheWeek[2]
                                )}
                                size="small"
                              />
                            }
                            label={t("ConductOutreachPage.tuesdayLabel")}
                          />
                          <MuiFormControlLabel
                            control={
                              <MuiCheckbox
                                name={"scheduleRecurringCheckbox"}
                                color="pepGray"
                                value={DaysOfTheWeek[3]}
                                onChange={handleChange}
                                checked={values.scheduleRecurringCheckbox?.includes(
                                  DaysOfTheWeek[3]
                                )}
                                size="small"
                              />
                            }
                            label={t("ConductOutreachPage.wednesdayLabel")}
                          />
                          <MuiFormControlLabel
                            control={
                              <MuiCheckbox
                                name={"scheduleRecurringCheckbox"}
                                color="pepGray"
                                value={DaysOfTheWeek[4]}
                                onChange={handleChange}
                                checked={values.scheduleRecurringCheckbox?.includes(
                                  DaysOfTheWeek[4]
                                )}
                                size="small"
                              />
                            }
                            label={t("ConductOutreachPage.thursdayLabel")}
                          />
                          <MuiFormControlLabel
                            control={
                              <MuiCheckbox
                                name={"scheduleRecurringCheckbox"}
                                color="pepGray"
                                value={DaysOfTheWeek[5]}
                                onChange={handleChange}
                                checked={values.scheduleRecurringCheckbox?.includes(
                                  DaysOfTheWeek[5]
                                )}
                                size="small"
                              />
                            }
                            label={t("ConductOutreachPage.fridayLabel")}
                          />
                          <MuiFormControlLabel
                            control={
                              <MuiCheckbox
                                name={"scheduleRecurringCheckbox"}
                                color="pepGray"
                                value={DaysOfTheWeek[6]}
                                onChange={handleChange}
                                checked={values.scheduleRecurringCheckbox?.includes(
                                  DaysOfTheWeek[6]
                                )}
                                size="small"
                              />
                            }
                            label={t("ConductOutreachPage.saturdayLabel")}
                          />
                        </MuiFormGroup>
                      </MuiFormControl>
                    </>
                  )}
                  <MuiGrid
                    container
                    sx={conductOutreachPageContentStyles.formButton}
                    justifyContent="flex-end"
                  >
                    <MuiGrid size={2}>
                      <MuiButton
                        color="primary"
                        onClick={() => handleClickReset(resetForm)}
                      >
                        {t(`formLabel.buttonReset`)}
                      </MuiButton>
                    </MuiGrid>
                    <MuiGrid container justifyContent="flex-end" size={2}>
                      <div style={conductOutreachPageContentStyles.wrapper}>
                        <MuiButton
                          variant="contained"
                          color="primary"
                          type="submit"
                          disabled={
                            !isValid ||
                            !dirty ||
                            isSubmitting ||
                            prospectCounts.length === 0 ||
                            conductOutreachLoader ||
                            study?.isStudySelfServiceActivated
                          }
                        >
                          {t(`formLabel.submitButtonLabel`)}
                        </MuiButton>
                        {conductOutreachLoader && (
                          <MuiCircularProgress
                            size={24}
                            sx={conductOutreachPageContentStyles.buttonProgress}
                          />
                        )}
                      </div>
                    </MuiGrid>
                  </MuiGrid>
                </MuiGrid>
              </MuiGrid>
            </Form>
          );
        }}
      </Formik>
      <MuiGrid size={12} sx={conductOutreachPageContentStyles.gridContainer}>
        <MuiTypography variant="h6">
          {t(`ConductOutreachPage.recurringOutreachLabel`)}
        </MuiTypography>
      </MuiGrid>

      <MuiBox
        id="pep-my-studies-table-box"
        sx={{ width: "100%", overflow: "auto", marginBottom: "20px" }}
      >
        <MuiDataGrid
          slots={{
            loadingOverlay: LoadingGridOverlay,
            noRowsOverlay: NoStudyOverlay,
          }}
          rows={recurringOutreach}
          getRowId={(row) => row.id}
          columns={defaultTableColumnHeaders(
            t,
            handleClickCancelRecurring,
            setCancelRecurringOutreachRow
          )}
          autoHeight
          disableColumnMenu
          disableColumnResize
          disableColumnReorder
          disableMultipleColumnsSorting
          disableRowSelectionOnClick
          loading={isLoading[LOADER_TYPES.OUTREACH_RECURRING_DATA]}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd"
          }
          sx={tableStyles}
          columnHeaderHeight={50}
          getRowHeight={() => "auto"}
        />
      </MuiBox>
      <MuiGrid
        container
        size={12}
        sx={conductOutreachPageContentStyles.customMarginBottom}
      >
        <MuiTypography variant="h6">
          {t(`ConductOutreachPage.historyLabel`)}
        </MuiTypography>
        {study.isHavingErrorSummary && (
          <MuiGrid container>
            <MuiButton
              variant="outlined"
              onClick={() => setOpenOutreachFailureSummaryDialog(true)}
            >
              <MuiErrorOutlineIcon fontSize="small" color="error" />
              &nbsp;
              {t(`ConductOutreachPage.outreachAutomatedErrorLabel`)}
            </MuiButton>
          </MuiGrid>
        )}
      </MuiGrid>
      <MuiPaper sx={conductOutreachPageContentStyles.customMarginBottom}>
        <MuiDataGrid
          slots={{
            loadingOverlay: LoadingGridOverlay,
            noRowsOverlay: NoStudyOverlay,
          }}
          rows={
            (outreachHistory &&
              addIndexToElements(outreachHistory, rowsPerPage, page) &&
              addTotalRowToElement(outreachHistory, study)) ??
            []
          }
          getRowId={(row) => row.id}
          columns={defaultTableColumnHeadersHistory(
            t,
            displayFailureSummaryDialog,
            handleOutreachErrorContent,
            failureSummaryDialog,
            failedOutreach,
            closeFailureSummaryDialog,
            errorsList,
            errorSummaryContent,
            isLoading,
            displayOutreachQuantityDialog,
            openDialog,
            selectedOutreach,
            closeOutreachQuantityDialog,
            study,
            refresh,
            setRefresh,
            handleClickCancel,
            setCancelOutreachRow
          )}
          autoHeight
          pagination
          disableColumnMenu
          disableColumnResize
          disableColumnReorder
          disableMultipleColumnsSorting
          disableRowSelectionOnClick
          loading={isLoading[LOADER_TYPES.OUTREACH_HISTORY_DATA]}
          paginationMode="server"
          sortingMode="server"
          pageSizeOptions={[5, 10, 20, 50]}
          paginationModel={{
            page: page,
            pageSize: rowsPerPage,
          }}
          rowCount={outreachHistoryTotalCount}
          onPaginationModelChange={handlePaginationModelChange}
          sortModel={[{ field: sort.orderBy, sort: sort.order }]}
          sortingOrder={["asc", "desc"]}
          onSortModelChange={handleSortChange}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd"
          }
          sx={tableStyles}
          columnHeaderHeight={90}
          getRowHeight={() => "auto"}
        />
      </MuiPaper>
    </div>
  ) : null;
};

export default ConductOutreachPageContent;
